<script setup lang="ts">
import { useField, useForm } from 'vee-validate';
import { useI18n } from 'vue-i18n';
import {
    VForm,
    VBtn,
    VTextarea,
    VAutocomplete,
    VCheckbox,
    VTextField,
} from 'vuetify/components';
import { VDateInput } from 'vuetify/labs/VDateInput';
import { toTypedSchema } from '@vee-validate/zod';
import { requirements } from '@/utils/formRequirements';
import { string, object, literal, boolean, date } from 'zod';
import type { NonNullableFields } from '@/utils';
import type { User } from '@/gql/graphql';
import { useLogout } from '@/composables/logout';
import { useFormSession } from '@/composables/form-session';
import { onBeforeUnmount } from 'vue';
import { FormName } from '@/composables/form-session';

export interface BottleneckEscalateFormValues {
    name: string;
    description: string;
    resolutionDate: Date | null;
    solution: string;
    assignedUser: string | null;
    escalationReason: string;
    sendNotification: boolean;
}

const props = defineProps<{
    escalationList: Pick<User, 'fullName' | 'id'>[];
    initialValues?: {
        name: string;
        description: string;
        resolutionDate: Date | null;
        solution: string | null;
        assignedUser: string;
        escalationReason: string;
        sendNotification: boolean;
    };
    id: string;
    disabled?: boolean;
    loading?: boolean;
}>();

const emit = defineEmits<{
    submit: [values: NonNullableFields<BottleneckEscalateFormValues>];
    cancel: [];
}>();

const { t } = useI18n({
    messages: {
        nl: {
            escalate: 'Escaleer',
            name: 'Naam',
            description: 'Omschrijving',
            resolution_date: 'Verwachte opleverdatum',
            escalation_reason: 'Verzoek aan verantwoordelijke',
            solution: 'Verwachte oplossing',
            send_notification: 'Stuur e-mail notificatie',
        },
        en: {
            escalate: 'Escalate',
            name: 'Name',
            description: 'Description',
            resolution_date: 'Expected delivery date',
            escalation_reason: 'Request to responsible person',
            solution: 'Expected solution',
            send_notification: 'Send email notification',
        },
    },
});

const { onBeforeLogout } = useLogout();

const { bottleneck } = requirements;
const {
    session: formSession,
    createFormSave,
    clearFormSave,
    onResult: onFormSessionResult,
} = useFormSession();

onBeforeLogout(async () => {
    if (!meta.value.dirty) return;

    await createFormSave({
        name: FormName.bottleneck_escalate,
        queryParams: [['id', props.id]],
        fields: {
            ...values,
        },
    });
}, 'form-save');

onBeforeUnmount(() => {
    clearFormSave();
});

const validationSchema = object({
    name: string()
        .min(bottleneck.name.min, t('error.required_field'))
        .max(bottleneck.name.max, t('error.maximum_characters', { max: bottleneck.name.max })),
    description: string()
        .min(bottleneck.description.min, t('error.required_field'))
        .max(bottleneck.description.max, t('error.maximum_characters', { max: bottleneck.description.max }))
        .or(literal(null).refine(() => false, t('error.required_field'))),
    resolutionDate: date({ required_error: t('error.required_field') }).or(
        literal(null).refine(() => false, t('error.required_field'))
    ),
    assignedUser: string().min(1),
    escalationReason: string()
        .min(bottleneck.escalationReason.min, t('error.required_field'))
        .max(
            bottleneck.escalationReason.max,
            t('error.maximum_characters', {
                max: bottleneck.escalationReason.max,
            })
        ),
    solution: string()
        .min(bottleneck.solution.min, t('error.required_field'))
        .max(
            bottleneck.solution.max,
            t('error.maximum_characters', { max: bottleneck.solution.max })
        )
        .or(literal(null).refine(() => false, t('error.required_field'))),
    sendNotification: boolean(),
});

const { handleSubmit, meta, values, setValues } = useForm({
    validationSchema: toTypedSchema(validationSchema),
    initialValues: props.initialValues
        ? props.initialValues
        : {
              name: '',
              description: '',
              resolutionDate: null,
              solution: '',
              assignedUser: '',
              escalationReason: '',
              sendNotification: false,
          },
});

const name = useField<string>('name');
const description = useField<string>('description');
const resolutionDate = useField<Date>('resolutionDate');
const solution = useField<string>('solution');
const assignedUser = useField<string>('assignedUser');
const escalationReason = useField<string>('escalationReason');
const sendNotification = useField<boolean>('sendNotification');

const submit = handleSubmit((values) => {
    emit('submit', values as NonNullableFields<BottleneckEscalateFormValues>);
});

onFormSessionResult(async () => {
    if (!formSession.value?.name) return;
    if (!formSession.value.fields) return;

    setValues({
        ...formSession.value.fields,
    });
});
</script>

<template>
    <v-form class="d-flex flex-column ga-3" :disabled @submit.prevent="submit">
        <VTextField
            v-model="name.value.value"
            :label="t('name')"
            :error-messages="name.errorMessage.value"
            autofocus
        />
        <VTextarea
            v-model="description.value.value"
            :label="t('description')"
            :error-messages="description.errorMessage.value"
            :counter="requirements.bottleneck.description.max"
            persistent-placeholder
            rows="5"
        />
        <VDateInput
            v-model="resolutionDate.value.value"
            :label="t('resolution_date')"
            :error-messages="resolutionDate.errorMessage.value"
            :min="new Date()"
            placeholder="dd-mm-yyyy"
            variant="outlined"
            prepend-icon=""
            hide-actions
            clearable
        />
        <v-autocomplete
            v-model="assignedUser.value.value"
            :items="escalationList"
            :label="t('responsible')"
            item-title="fullName"
            item-value="id"
            :error-messages="assignedUser.errorMessage.value"
            :loading
            autofocus
        />
        <v-textarea
            v-model="solution.value.value"
            :label="t('solution')"
            :error-messages="solution.errorMessage.value"
            :counter="requirements.bottleneck.solution.max"
            persistent-placeholder
            rows="5"
        />
        <v-textarea
            v-model="escalationReason.value.value"
            :label="t('escalation_reason')"
            :error-messages="escalationReason.errorMessage.value"
            :counter="requirements.bottleneck.escalationReason.max"
            persistent-placeholder
            rows="5"
        ></v-textarea>
        <v-checkbox
            v-model="sendNotification.value.value"
            :label="t('send_notification')"
        />
        <div class="d-flex justify-end ga-3 mt-5">
            <v-btn variant="text" @click="$emit('cancel')">
                {{ t('cancel') }}
            </v-btn>
            <v-btn
                type="submit"
                variant="elevated"
                :disabled="!meta.valid"
                :loading
            >
                {{ t('escalate') }}
            </v-btn>
        </div>
    </v-form>
</template>
